import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DOMPurify from 'dompurify';
import { getMaterial } from '../../../redux/materialSlice';
import { getMainConfig, updateMainConfig } from '../../../redux/stepsSlice';
import { getValue } from '../utils/tools';
import { getProfile } from '../../../redux/profileSlice';
import { getAudienceData, getCompanyDetails } from '../../../redux/hubSlice';
import { getCompany } from '../../../redux/companySlice';
import InlineRichText from './components/authoring/v2/InlineRichText';

// Toolbar for inline hero editing — color, size, weight, alignment, line breaks.
const HERO_TOOLBAR = [
  [{ size: ['small', false, 'large', 'huge'] }],
  ['bold', 'italic', 'underline'],
  [{ color: [] }],
  [{ align: [] }],
  ['clean'],
];

const PreviewHeader = ({ default_host, audience, forcePublicView = false, editable = false }) => {
  const dispatch = useDispatch();
  const material = useSelector((state) => getMaterial(state));
  const currentUser = useSelector((state) => getProfile(state));
  const audienceDetail = useSelector((state) => getAudienceData(state));
  const hubCompanyDetail = useSelector((state) => getCompanyDetails(state));
  const adminCompanyDetail = useSelector((state) => getCompany(state));
  const mainConfig = useSelector((state) => getMainConfig(state));

  const hubUrl = window.location.pathname.split('/')[2];
  const isHub = hubUrl === 'hub';
  const isPublicView = forcePublicView || isHub;
  const companyDetail = isHub ? hubCompanyDetail : adminCompanyDetail;

  const persistedConfig = material?.config;
  const config = isPublicView
    ? persistedConfig
    : (Array.isArray(mainConfig) && mainConfig.length > 0 ? mainConfig : persistedConfig);

  const defaultColors = {
    primary: '#3BA4B4',
    background: '#F6F8F9',
    text: '#363841',
    btnColor: '#3BA4B4',
    btnTextColor: '#FFFFFF',
    config
  };

  const headline = getValue(defaultColors, 'phc', 'phcHeadline');
  const subHeaderDescription = getValue(defaultColors, 'phc', 'phcSubHeaderDescription');
  const headerLogo = config ? getValue(defaultColors, 'phc', 'phcHeaderLogo') : '';
  const coverImage = config ? getValue(defaultColors, 'phc', 'phcCoverImage') : '';

  const haShowSignIn = getValue(defaultColors, 'ha', 'haShowSignIn');
  const haBGColor = getValue(defaultColors, 'ha', 'haBGColor');
  const haTextColor = getValue(defaultColors, 'ha', 'haTextColor');
  const haFont = getValue(defaultColors, 'ha', 'haFont');

  const hasCookie = (cookieName) => {
    if (typeof document === 'undefined') return false;
    return document.cookie.split(';').some((cookie) => cookie.trim().startsWith(`${cookieName}=`));
  };

  const getCookieValue = (cookieName) => {
    if (typeof document === 'undefined') return '';
    const entry = document.cookie
      .split('; ')
      .find((cookie) => cookie.startsWith(`${cookieName}=`));
    return entry ? decodeURIComponent(entry.split('=')[1]) : '';
  };

  const hasMagicLinkAudToken = () => {
    const audToken = getCookieValue('aud_token');
    if (!audToken) return false;

    try {
      const payloadPart = audToken.split('.')[1];
      if (!payloadPart) return false;
      const normalized = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = JSON.parse(atob(normalized));
      return jsonPayload?.sign_in_method === 'magic_link' && Boolean(jsonPayload?.email);
    } catch (_error) {
      return false;
    }
  };

  const signedInAudienceEmail = audienceDetail?.email || audience?.email;
  const hasMagicLinkSession = hasCookie('session_expiration_time') || hasMagicLinkAudToken();
  const isSignedIn = isHub
    ? Boolean(signedInAudienceEmail || hasMagicLinkSession)
    : Boolean(Object.keys(currentUser || {}).length > 0);

  const displayInfo = isHub ? (signedInAudienceEmail || 'Learner') : currentUser?.name;
  const logoImage = headerLogo || companyDetail?.logo_url;

  const handleSignIn = () => {
    if (!isHub) return;
    const currentPath = `${window.location.pathname}${window.location.search || ''}`;
    const returnTo = encodeURIComponent(currentPath);
    const path = `/${companyDetail?.slug}/passwordless_sign_in?return_to=${returnTo}`;
    window.location.href = path;
  };

  const heroStyle = {
    '--path-hero-bg': haBGColor || '#2D3E50',
    '--path-hero-text': haTextColor || '#FFFFFF',
    '--path-hero-font': haFont || 'Open Sans',
    backgroundColor: haBGColor || '#2D3E50'
  };

  if (coverImage) {
    heroStyle.backgroundImage = `linear-gradient(180deg, rgba(17, 24, 39, 0.18), rgba(17, 24, 39, 0.35)), url(${coverImage})`;
    heroStyle.backgroundSize = 'cover';
    heroStyle.backgroundPosition = 'center top';
    heroStyle.backgroundRepeat = 'no-repeat';
  }

  const title = headline || material.name;
  const titleMarkup = DOMPurify.sanitize(title || '');
  const description = subHeaderDescription || material.description;
  const descriptionMarkup = typeof description === 'string' && /<\/?[a-z][\s\S]*>/i.test(description)
    ? DOMPurify.sanitize(description)
    : '';
  const showSignedInState = isHub && isSignedIn;

  const saveHeadline = (html) => dispatch(updateMainConfig({ sectionId: 'phc', titleId: 'phcHeadline', value: html }));
  const saveSubHeader = (html) => dispatch(updateMainConfig({ sectionId: 'phc', titleId: 'phcSubHeaderDescription', value: html }));

  return (
    <section className={`preview-header ${isPublicView ? 'is-public-view' : 'is-admin-preview'}`} style={heroStyle}>
      <div className="preview-header__backdrop" />
      <div className="preview-header__content">
        {logoImage && (
          <div className="preview-header__logo-frame">
            <img src={logoImage} className="preview-header__logo" alt={companyDetail?.name || 'Company logo'} />
          </div>
        )}

        <div className="preview-header__copy">
          {editable ? (
            <>
              <InlineRichText
                editable
                className="preview-header__title"
                value={headline || ''}
                onSave={saveHeadline}
                placeholder={material.name || 'Add a headline'}
                toolbar={HERO_TOOLBAR}
              />
              <InlineRichText
                editable
                className="preview-header__description"
                value={subHeaderDescription || ''}
                onSave={saveSubHeader}
                placeholder={material.description || 'Add a sub-header'}
                toolbar={HERO_TOOLBAR}
              />
            </>
          ) : (
            <>
              <div
                className="preview-header__title"
                dangerouslySetInnerHTML={{ __html: titleMarkup }}
              />
              {description && (
                descriptionMarkup ? (
                  <div
                    className="preview-header__description"
                    dangerouslySetInnerHTML={{ __html: descriptionMarkup }}
                  />
                ) : (
                  <p className="preview-header__description">{description}</p>
                )
              )}
            </>
          )}
        </div>

        <div className="preview-header__actions">
          {showSignedInState ? (
            <div className="preview-header__identity" aria-live="polite">
              <span className="preview-header__identity-label">Signed in as</span>
              <span className="preview-header__identity-value">{displayInfo}</span>
            </div>
          ) : haShowSignIn !== false ? (
            <button
              type="button"
              className={`preview-header__sign-in ${!isHub ? 'is-preview-only' : ''}`}
              onClick={handleSignIn}
            >
              Sign In
            </button>
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default PreviewHeader;
